import { useState } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Tally Counter — multiple named counters. Per the brief: state lives in
// component only (refresh resets), with optional CSV export so the user
// can save their count if they want.

const DEFAULT_CHANNELS = [
  { name: "Channel 1", count: 0 },
  { name: "Channel 2", count: 0 },
  { name: "Channel 3", count: 0 },
];

export default function TallyCounter() {
  const [channels, setChannels] = useState(DEFAULT_CHANNELS);
  const [editingIdx, setEditingIdx] = useState(-1);

  const update = (i, delta) => {
    const next = [...channels];
    next[i] = { ...next[i], count: Math.max(0, next[i].count + delta) };
    setChannels(next);
  };
  const rename = (i, name) => {
    const next = [...channels];
    next[i] = { ...next[i], name };
    setChannels(next);
  };
  const reset = (i) => update(i, -channels[i].count);
  const resetAll = () => setChannels(channels.map((c) => ({ ...c, count: 0 })));
  const add = () => {
    if (channels.length >= 10) return;
    setChannels([...channels, { name: `Channel ${channels.length + 1}`, count: 0 }]);
  };
  const remove = (i) => {
    if (channels.length <= 1) return;
    setChannels(channels.filter((_, idx) => idx !== i));
  };

  const exportCSV = () => {
    const rows = ["Channel,Count", ...channels.map((c) => `${c.name.replace(/,/g, " ")},${c.count}`)];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tally-counts.csv";
    a.click();
  };

  const total = channels.reduce((s, c) => s + c.count, 0);

  return (
    <ToolsShell title="Tally Counter">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Multiple labeled counters. Tap to count; rename channels by tapping the name. Refresh clears counts — export to CSV first if you want to keep them.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {channels.map((c, i) => (
          <div key={i} style={{ background: "#13141F", border: "1px solid #262839", borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              {editingIdx === i ? (
                <input
                  autoFocus
                  value={c.name}
                  onChange={(e) => rename(i, e.target.value)}
                  onBlur={() => setEditingIdx(-1)}
                  onKeyDown={(e) => { if (e.key === "Enter") setEditingIdx(-1); }}
                  style={{ flex: 1, background: "#0F1018", border: "1px solid #262839", borderRadius: 6, color: "#E8E9F3", fontSize: 14, padding: "6px 8px" }}
                />
              ) : (
                <button onClick={() => setEditingIdx(i)} style={{ flex: 1, background: "transparent", border: "none", color: "#E8E9F3", fontSize: 14, textAlign: "left", cursor: "pointer", padding: 0 }}>
                  {c.name}
                </button>
              )}
              <button onClick={() => remove(i)} disabled={channels.length === 1} style={{ background: "transparent", color: "#5B5E7A", border: "none", fontSize: 16, cursor: channels.length > 1 ? "pointer" : "not-allowed", padding: "0 6px" }}>×</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button onClick={() => update(i, -1)} style={{ flex: 1, background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 10, padding: "16px 0", fontSize: 22, fontWeight: 700, cursor: "pointer" }}>−</button>
              <div style={{ fontSize: 32, fontWeight: 800, color: TOOLS_ACCENT, minWidth: 70, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{c.count}</div>
              <button onClick={() => update(i, 1)} style={{ flex: 1, background: TOOLS_ACCENT, color: "#1A0604", border: "none", borderRadius: 10, padding: "16px 0", fontSize: 22, fontWeight: 700, cursor: "pointer" }}>+</button>
            </div>
            {c.count > 0 && (
              <button onClick={() => reset(i)} style={{ marginTop: 8, background: "transparent", color: "#5B5E7A", border: "none", fontSize: 12, cursor: "pointer" }}>Reset</button>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
        <button onClick={add} disabled={channels.length >= 10} style={{ background: "#1C1E2E", color: channels.length >= 10 ? "#5B5E7A" : TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: channels.length < 10 ? "pointer" : "not-allowed" }}>
          + Add channel
        </button>
        <button onClick={resetAll} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>Reset all</button>
        <button onClick={exportCSV} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer", marginLeft: "auto" }}>Export CSV</button>
      </div>

      <div style={{ marginTop: 18, fontSize: 13, color: "#7C8099", textAlign: "center" }}>
        Total across channels: <strong style={{ color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{total}</strong>
      </div>
    </ToolsShell>
  );
}
